import { useState } from 'react';

import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import {
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormHelperText,
} from '@mui/material';
import { useFormik } from 'formik';
import { useNotify, useRefresh, useUpdate, useGetList, useRecordContext, Button as ActionButton } from 'react-admin';
import { DataProviderResource } from 'src/constants';
import { OfferDto } from 'src/dtos';
import { HttpErrorTypography } from 'src/lib';
import { UserDetails } from 'src/models';
import * as yup from 'yup';

interface PostcloseInvestFormValues {
  offerIds: string[];
}

const validationSchema = yup.object().shape({
  offerIds: yup.array().required('Postclose Offer is required'),
});

export const PostcloseOfferInvestUserActionButton = () => {
  const user = useRecordContext<UserDetails>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isError, error, isLoading }] = useUpdate();

  const [open, setOpen] = useState(false);

  const {
    data,
    isLoading: offersLoading,
    error: offerError,
  }: { data: any; isLoading: boolean; error: any } = useGetList<OfferDto>('offers', {
    filter: { status: 'Closed', visibleToEndUsers: true },
    meta: { skipPagination: true },
  });

  const defaultCheckedValues = data
    ?.filter((anOffer: OfferDto) => user?.postcloseOffers?.includes(anOffer.id))
    .map((anOffer: OfferDto) => anOffer.id);

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = (values: PostcloseInvestFormValues) => {
    update(
      DataProviderResource.UpdateUserPostcloseOffers,
      { id: user?.id, data: { offerIds: values.offerIds } },
      {
        mutationMode: 'pessimistic',
        onSuccess: () => {
          notify(`Postclose offer investment successfully updated.`, {
            type: 'success',
            undoable: true,
          });
          handleClose();
        },
        onSettled: () => {
          refresh();
        },
      },
    );
  };

  const formik = useFormik<PostcloseInvestFormValues>({
    initialValues: {
      offerIds: defaultCheckedValues ?? [],
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: handleSubmit,
  });

  if (offersLoading) {
    return <CircularProgress />;
  }

  return (
    <>
      <ActionButton
        label='Postclose Invest'
        onClick={() => setOpen(true)}
        disabled={isLoading}
        style={{ marginTop: '8px' }}>
        <AddShoppingCartIcon />
      </ActionButton>
      <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
        <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
          <DialogTitle>Postclose Offer Investment</DialogTitle>
          <DialogContent>
            <Grid container direction='column' spacing={2}>
              <Grid item xs={6} md={6}>
                <FormControl component='fieldset' fullWidth error={formik.touched.offerIds && !!formik.errors.offerIds}>
                  <FormLabel component='legend'>Closed Offer List</FormLabel>
                  <FormGroup>
                    {(data ?? []).map((anOffer: OfferDto) => {
                      const id = anOffer.id;
                      const labelText = (anOffer as any).title ?? (anOffer as any).name ?? id;
                      const checked = formik.values.offerIds?.includes(id);

                      return (
                        <FormControlLabel
                          key={id}
                          control={
                            <Checkbox
                              checked={checked}
                              onChange={() => {
                                const current = formik.values.offerIds ?? [];
                                const next = current.includes(id) ? current.filter(x => x !== id) : [...current, id];
                                formik.setFieldValue('offerIds', next);
                              }}
                              disabled={isLoading}
                              name='offerIds'
                              value={id}
                            />
                          }
                          label={labelText}
                        />
                      );
                    })}
                  </FormGroup>
                  {formik.touched.offerIds && formik.errors.offerIds && (
                    <FormHelperText>{formik.errors.offerIds as unknown as string}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid container direction='column' spacing={2}>
                <Grid item md={12} sm={12}>
                  {isError && <HttpErrorTypography error={error || offerError} />}
                </Grid>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Grid item xs={12} container direction='row' justifyContent='flex-end' spacing={2}>
              <Grid item>
                <Button variant='outlined' color='inherit' size='medium' onClick={handleClose}>
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <Button variant='contained' color='primary' size='medium' disabled={isLoading} type='submit'>
                  Save
                </Button>
              </Grid>
            </Grid>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};
